import { matchPath } from './router';

/**
 * Where to go after signing in: the `next` a signed-out deep link was parked
 * under (#922 — the partner's lead email links to `/leads/:id`, and a
 * dispatcher who is not signed in must land on that lead, not the home
 * screen). Same-origin paths only: `//evil.test` and `/\evil.test` are
 * protocol-relative to a browser, so anything but a single leading `/` is
 * ignored, as are the public pages and the sign-in routes themselves.
 */
export function safeNext(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  if (raw.includes('\\') || [...raw].some((ch) => ch.charCodeAt(0) < 0x20)) return null;
  const path = raw.split(/[?#]/)[0] ?? '';
  if (path === '/' || path === '/sign-in' || path === '/welcome') return null;
  if (matchPath('/l/:token', path) || matchPath('/share/:token', path)) return null;
  return raw;
}

/** `/sign-in?next=…` for a signed-out visit to `path` (plus its query). */
export function signInFor(path: string, search: URLSearchParams): string {
  const query = search.toString();
  const next = safeNext(`${path}${query ? `?${query}` : ''}`);
  return next ? `/sign-in?next=${encodeURIComponent(next)}` : '/sign-in';
}

/**
 * Where a person goes once they have created their organisation: back to an
 * AI agent's sign-in (/oauth/consent) if that is what sent them to sign up,
 * otherwise onboarding. Only agent sign-ins resume; any other deep link means
 * little to an organisation that is a minute old.
 */
export function afterOrgSetup(path: string, search: URLSearchParams): string {
  const query = search.toString();
  const resume = safeNext(path === '/sign-in' ? search.get('next') : `${path}${query ? `?${query}` : ''}`);
  return resume?.startsWith('/oauth/') ? resume : '/setup/trades';
}
